// pipe (scan intake): the global sidebar Scan entry. Follows sites-button.tsx's affordance shape
// but as a plain MenuItem that OPENS THE SCAN DIALOG instead of navigating (the
// invite-members-button.tsx onClick pattern). Gated on the shared useErpProvisioning SSOT:
// hidden until `/me/provisioning` resolves (fail-closed) — an external / no-ERP session never
// resolves it, so those users never see the entry; the Core API re-checks every scan lookup
// regardless. This file is the DI seam: it wires ErpService.scanLookup + workbench.open into the
// PURE ScanDialog (mounting the entry in the sidebar is the sidebar's own wiring, not done here).
import { useI18n } from '@affine/i18n';
import { CameraIcon } from '@blocksuite/icons/rc';
import { useService, useServices } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { MenuItem } from '../../modules/app-sidebar/views';
import {
  ErpService,
  type RecordRow,
  useErpProvisioning,
} from '../../modules/erp';
import { WorkbenchService } from '../../modules/workbench';
import {
  containRelationBody,
  hostAssetPatchBody,
  placementCreateBody,
  ScanDialog,
} from './scan-dialog';

export { ScanDialog } from './scan-dialog';

export const SidebarScanButton = () => {
  const t = useI18n();
  const provisioning = useErpProvisioning();
  const erp = useService(ErpService);
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const lookup = useCallback((code: string) => erp.scanLookup(code), [erp]);
  const transition = useCallback(
    (id: string, to: string) => erp.transition(id, to),
    [erp]
  );
  // IP6.3 put-away: ONE audited create through the shared entity chokepoint. NO facilityId — the
  // server derives the placement's site from the bin and refuses a cross-facility bin with an
  // explicit message (surfaced inline by the dialog).
  const createPlacement = useCallback(
    (item: RecordRow, bin: RecordRow) => {
      const body = placementCreateBody(item, bin);
      return erp.createRecord(body.type, body.title, body.data);
    },
    [erp]
  );
  // IP7.1 set-host: ONE shallow-merge PATCH through the shared update chokepoint — only
  // `host_asset_id` changes (title: null carries the stored title forward). The server
  // re-validates the parent (`revalidate_parent`) and its refusal surfaces inline.
  const setHostAsset = useCallback(
    (drive: RecordRow, asset: RecordRow) => {
      const body = hostAssetPatchBody(asset);
      return erp.updateRecord(drive.id, body.title, body.data);
    },
    [erp]
  );
  // IP7.2 add-to-pallet: ONE `contained_in` relation through the cookie /api/relations twin of
  // the one link_core chokepoint (Erp-write-gated, both endpoints visibility-checked, the
  // data-bearing link guard refuses with an explicit message the dialog surfaces).
  const addToPallet = useCallback(
    (item: RecordRow, container: RecordRow) => {
      const body = containRelationBody(item, container);
      return erp.createRelation(body.src, body.dst, body.rel);
    },
    [erp]
  );
  const navigate = useCallback((to: string) => workbench.open(to), [workbench]);

  // The gate: hidden unless the actor is ERP-provisioned (fail-closed on null — unresolved,
  // failed, or an external surface all stay hidden; no flash).
  if (!provisioning) {
    return null;
  }

  return (
    <>
      <MenuItem icon={<CameraIcon />} onClick={handleOpen}>
        <span data-testid="pipe-scan-nav">{t['com.affine.pipe.scan.nav']()}</span>
      </MenuItem>
      {open ? (
        // Mounted only while open: closing UNMOUNTS the dialog, so its cleanup releases the
        // camera and every reopen starts from a clean scan box.
        <ScanDialog
          open
          onClose={handleClose}
          lookup={lookup}
          transition={transition}
          createPlacement={createPlacement}
          setHostAsset={setHostAsset}
          addToPallet={addToPallet}
          onNavigate={navigate}
        />
      ) : null}
    </>
  );
};
